<script lang="ts" setup>
  import { useToast } from '@solfacil/girassol';
  import type { Option } from '@solfacil/girassol/dist/types/components/forms/select/types';
  import { storeToRefs } from 'pinia';

  import { useRegionMultiCd } from '~/composables/regionMultiCd';
  import { STATES } from '~/constants';
  import type {
    GetByPartnerIdOut,
    ListCitiesByRegionQueryVariables,
  } from '~/types/ecommerce/generated.codegen';
  import {
    useListCitiesByRegionLazyQuery,
    useRemoveAllProductsCartMutation,
    useRemoveProductCartMutation,
    useUpdateSubregionMutation,
  } from '~/types/ecommerce/generated.codegen';

  const { track } = useTracker();
  const { createErrorToast } = useToast();
  const route = useRoute();
  const regionStore = useRegionStore();
  const { multiCd } = useRegionMultiCd();
  const { region, subregionIbge, subregionName } = storeToRefs(regionStore);
  const regionInput = ref<string>(region.value || '');
  const subregionOptionValue = ref<string>(subregionIbge.value || '');
  const subregionNameInput = ref<string>(subregionName.value || '');

  const subregionOptions = ref<Option[]>([]);
  const zipcodeBySubregionOptionValue = ref<Record<string, string>>({});
  const {
    onResult: onListCitiesByRegionResult,
    loading: loadingCities,
    load: loadCities,
  } = useListCitiesByRegionLazyQuery(undefined, {
    fetchPolicy: 'network-only',
  });
  onListCitiesByRegionResult(result => {
    const list = result.data?.listCitiesByRegion?.cities;
    if (list == null) {
      return;
    }
    const zipcodeMap: Record<string, string> = {};
    subregionOptions.value = list.map(row => {
      const value = String(row.ibge_code || row.name);
      zipcodeMap[value] = row.first_cep;
      return {
        name: row.name,
        value: row.ibge_code || row.name,
      };
    });
    zipcodeBySubregionOptionValue.value = {
      ...zipcodeBySubregionOptionValue.value,
      ...zipcodeMap,
    };
  });

  const subregionSearchTerm = ref('');

  function listCitiesVars(
    options: { searchText?: string } = {},
  ): ListCitiesByRegionQueryVariables | null {
    if (!regionInput.value) {
      return null;
    }
    const base: ListCitiesByRegionQueryVariables = {
      region: regionInput.value.toUpperCase(),
      ordered: true,
      capitalFirst: true,
      limit: 50,
    };
    const term = options.searchText?.trim();
    if (term && term.length >= 3) {
      return { ...base, term };
    }
    return base;
  }

  const fetchCitiesDebounced = useDebounceFn(() => {
    const vars = listCitiesVars({
      searchText: subregionSearchTerm.value,
    });
    if (vars) {
      void loadCities(undefined, vars);
    }
  }, 500);

  function fetchSubregionOptionsForRegion() {
    const vars = listCitiesVars();
    if (vars) {
      void loadCities(undefined, vars);
    }
  }

  function onSubregionSearchInput(raw?: string | null) {
    if (!regionInput.value) {
      return;
    }
    const trimmedCitySearch = (raw ?? '').trim();
    if (trimmedCitySearch.length >= 3) {
      subregionSearchTerm.value = trimmedCitySearch;
      fetchCitiesDebounced();
    } else if (trimmedCitySearch.length === 0) {
      fetchSubregionOptionsForRegion();
    }
  }
  const regionModal = inject<Ref<boolean>>('regionSelectModal')!;
  const regionSelectOpen = ref(false);
  const subregionSelectOpen = ref(false);

  const isSelectOpen = computed(
    () => regionSelectOpen.value || subregionSelectOpen.value,
  );

  const clearCartModal = ref<boolean>(false);
  const cartData = inject<Ref<GetByPartnerIdOut>>('cart')!;
  const setCart = inject<(value: GetByPartnerIdOut) => void>('setCart');
  const userStore = useUserStore();
  const clearCartHeader = inject<() => void>('clearCartHeader')!;

  function extensionsDetailsCode(source: unknown): number | undefined {
    if (source == null) return undefined;
    try {
      const parsed = JSON.parse(JSON.stringify(source));
      const gqlErr = parsed.graphQLErrors?.[0] ?? parsed;
      const raw = gqlErr?.extensions?.details?.code;
      return raw != null ? Number(raw) : undefined;
    } catch {
      return undefined;
    }
  }

  function isIbgeLike(value: string) {
    return /^\d{6,8}$/.test(String(value ?? '').trim());
  }

  /** Atualiza o store a partir do carrinho (ex.: após updateSubregion). */
  function syncRegionStoreFromCart(
    cart: GetByPartnerIdOut,
    zipFallback?: string,
  ) {
    regionStore.clearDeliveryPreferenceFromUrl();
    const uf = String(cart.region ?? regionStore.region ?? '')
      .trim()
      .toUpperCase();
    if (!uf) {
      return;
    }

    const ibgeFromAddress = String(
      cart.shipping?.address?.ibgecode ?? '',
    ).trim();
    const cityFromAddress = String(cart.shipping?.address?.city ?? '').trim();
    const subFromCart = String(cart.subregion ?? '').trim();
    const ibgeFromModal = String(subregionOptionValue.value ?? '').trim();

    const ibgeResolved =
      (isIbgeLike(ibgeFromAddress) ? ibgeFromAddress : '') ||
      (isIbgeLike(subFromCart) ? subFromCart : '') ||
      (isIbgeLike(ibgeFromModal) ? ibgeFromModal : '');

    let zipResolved =
      String(zipFallback ?? '').trim() ||
      String(cart.zipcode ?? '').trim() ||
      String(cart.shipping?.address?.zipcode ?? '').trim() ||
      '';
    if (!zipResolved && ibgeResolved) {
      zipResolved = String(
        zipcodeBySubregionOptionValue.value[ibgeResolved] ?? '',
      ).trim();
    }
    if (!zipResolved) {
      zipResolved = String(regionStore.zipcode ?? '').trim();
    }

    if (multiCd.value) {
      const subregionName =
        (cityFromAddress && !isIbgeLike(cityFromAddress)
          ? cityFromAddress
          : '') ||
        (subFromCart && !isIbgeLike(subFromCart) ? subFromCart : '') ||
        String(subregionNameInput.value ?? '').trim();

      regionStore.setDeliveryRegionFields({
        region: uf,
        subregionName,
        subregionIbge: ibgeResolved,
        zipcode: zipResolved,
      });
    } else {
      const sub = subFromCart;
      regionStore.setDeliveryRegionFields({
        region: uf,
        subregionName: isIbgeLike(sub) ? '' : sub,
        subregionIbge: isIbgeLike(sub) ? sub : '',
        zipcode: zipResolved,
      });
    }

    track('modal_local_entrega_confirmar', {
      stateSelected: regionStore.region,
      cityId: regionStore.subregionIbge,
      cityName: regionStore.subregionName,
    });
  }

  const sortedStateOptions = computed(() =>
    [...STATES].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
  );

  const stateSearchTerm = ref('');

  const stateSelectOptionsFiltered = computed(() => {
    const sortedStates = sortedStateOptions.value;
    const stateSearchNeedle = stateSearchTerm.value.trim().toLowerCase();
    const filtered = stateSearchNeedle
      ? sortedStates.filter(
          row =>
            String(row.name).toLowerCase().includes(stateSearchNeedle) ||
            String(row.value).toLowerCase().includes(stateSearchNeedle),
        )
      : sortedStates;
    const selected = stateSelected.value;
    if (
      !selected ||
      filtered.some(option => String(option.value) === String(selected.value))
    ) {
      return filtered;
    }
    const pinnedOption = sortedStates.find(
      option => String(option.value) === String(selected.value),
    );
    return pinnedOption ? [pinnedOption, ...filtered] : filtered;
  });

  function onStateSearchInput(raw?: string | null) {
    stateSearchTerm.value = String(raw ?? '').trim();
  }

  function findStateOptionByRegion(deliveryRegion: string): Option | undefined {
    return deliveryRegion
      ? sortedStateOptions.value.find(row => row.value === deliveryRegion)
      : undefined;
  }

  const stateSelected = ref<Option | undefined>(
    findStateOptionByRegion(region.value || ''),
  );

  const subregionSelected = computed<Option | undefined>({
    get() {
      if (!subregionOptionValue.value) {
        return undefined;
      }
      return subregionOptions.value.find(
        option => String(option.value) === String(subregionOptionValue.value),
      );
    },
    set(next: Option | Option[] | undefined) {
      const opt = Array.isArray(next) ? next[0] : next;
      if (!opt) {
        subregionOptionValue.value = '';
        subregionNameInput.value = '';
        return;
      }
      track('modal_local_entrega_selecionar_cidade', {
        citySelected: opt.name,
        state: regionInput.value,
      });
      subregionOptionValue.value = String(opt.value);
      subregionNameInput.value = String(opt.name);
    },
  });

  const headerStateName = computed(() => {
    if (!region.value) {
      return '';
    }
    return STATES.find(s => s.value === region.value)?.name || region.value;
  });

  const headerSubregionName = computed(() => {
    if (!multiCd.value) {
      return '';
    }
    return (subregionName.value || '').trim();
  });

  const headerMobileLine = computed(() => {
    if (!headerStateName.value) {
      return '';
    }
    if (headerSubregionName.value) {
      return `${headerStateName.value}:\u00A0${headerSubregionName.value}`;
    }
    return headerStateName.value;
  });

  function syncModalFieldsFromStore() {
    regionInput.value = region.value;
    stateSelected.value = findStateOptionByRegion(region.value);
    subregionOptionValue.value = subregionIbge.value;
    subregionNameInput.value = subregionName.value;
  }

  watch([region, subregionIbge, subregionName], syncModalFieldsFromStore, {
    immediate: true,
  });

  function onRegionSelectOpen() {
    regionSelectOpen.value = true;
    stateSearchTerm.value = '';
  }

  function onRegionSelectClose() {
    regionSelectOpen.value = false;
    stateSearchTerm.value = '';
  }

  function onSubregionSelectOpen() {
    subregionSelectOpen.value = true;
    fetchSubregionOptionsForRegion();
  }

  function onSubregionSelectClose() {
    subregionSelectOpen.value = false;
  }

  watch(regionModal, isOpen => {
    if (!isOpen) {
      regionSelectOpen.value = false;
      subregionSelectOpen.value = false;
      stateSearchTerm.value = '';
    }
  });

  function handleLocationModal() {
    track(
      regionModal.value
        ? 'modal_local_entrega_cancelar'
        : 'header_local_entrega',
      {},
    );
    syncModalFieldsFromStore();
    regionModal.value = !regionModal.value;
  }

  function changeStateInput(state: Option | Option[] | undefined) {
    const opt = Array.isArray(state) ? state[0] : state;
    if (!opt) {
      return;
    }
    track('modal_local_entrega_selecionar_estado', {
      stateSelected: opt.name,
    });
    stateSelected.value =
      sortedStateOptions.value.find(
        row => String(row.value) === String(opt.value),
      ) ?? opt;
    regionInput.value = String(opt.value);
    stateSearchTerm.value = '';
    subregionOptionValue.value = '';
    subregionNameInput.value = '';
    zipcodeBySubregionOptionValue.value = {};
    subregionSelectOpen.value = false;
    subregionOptions.value = [];
  }

  const { mutate: updateSubregionMutate, loading: updateSubregionLoading } =
    useUpdateSubregionMutation({
      context: {
        headers: {
          'x-impersonate-id': userStore.personifiedUserId,
        },
      },
    });

  const mutationHeaders = {
    'x-impersonate-id': userStore.personifiedUserId,
  };

  async function changeLocation() {
    if (route.path === '/custom-kits') {
      regionModal.value = false;
      clearCartModal.value = true;
      return;
    }

    const cartId = cartData.value?.id;
    const cartExists = Boolean(cartId);
    const stateChanged =
      String(regionInput.value || '')
        .trim()
        .toUpperCase() !==
      String(region.value || '')
        .trim()
        .toUpperCase();

    if (stateChanged) {
      regionModal.value = false;
      if (cartExists) {
        clearCartModal.value = true;
      } else {
        updateRegion();
      }
      return;
    }

    if (!multiCd.value) {
      regionModal.value = false;
      updateRegion();
      return;
    }

    if (!cartExists) {
      regionModal.value = false;
      updateRegion();
      return;
    }

    const name = String(subregionNameInput.value ?? '').trim();
    const ibge = String(subregionOptionValue.value ?? '').trim();
    const zipcode = ibge ? zipcodeBySubregionOptionValue.value[ibge] || '' : '';
    const subregion = name || ibge;

    try {
      const result = await updateSubregionMutate(
        {
          cart_id: cartId!,
          subregion,
          zipcode,
        },
        {
          context: {
            headers: mutationHeaders,
          },
        },
      );

      if (result?.errors?.length) {
        const code = extensionsDetailsCode(result.errors[0]);
        if (code === 4100) {
          regionModal.value = false;
          clearCartModal.value = true;
          return;
        }
        createErrorToast({
          title: 'Oops..',
          description:
            result.errors[0]?.message ||
            'Não foi possível atualizar a região do carrinho.',
        });
        return;
      }

      regionModal.value = false;

      const updated = result?.data?.updateSubregion;
      if (!updated) {
        createErrorToast({
          title: 'Oops..',
          description: 'Não foi possível atualizar a região do carrinho.',
        });
        return;
      }

      setCart?.(updated as GetByPartnerIdOut);
      syncRegionStoreFromCart(updated as GetByPartnerIdOut, zipcode);
    } catch (error: unknown) {
      const code = extensionsDetailsCode(error);
      if (code === 4100) {
        regionModal.value = false;
        clearCartModal.value = true;
        return;
      }
      createErrorToast({
        title: 'Oops..',
        description: 'Não foi possível atualizar a região do carrinho.',
      });
    }
  }

  async function redirectToHome() {
    clearCartModal.value = false;
    updateRegion();
    clearCartHeader();
    const { sku: _skuCode, dc_id: _dcId, ...params } = await route.query;

    await navigateTo({
      name: 'index',
      query: { ...params },
    });
  }

  function updateRegion() {
    regionStore.clearDeliveryPreferenceFromUrl();
    const subregionIbgeVal = String(
      subregionSelected.value?.value ?? subregionOptionValue.value ?? '',
    ).trim();
    const subregionNameVal = String(
      subregionSelected.value?.name ?? subregionNameInput.value ?? '',
    ).trim();

    const deliveryRegion = regionInput.value as string;
    const useMulti = multiCd.value;
    const zipcode = useMulti
      ? String(
          zipcodeBySubregionOptionValue.value[subregionIbgeVal] ||
            regionStore.zipcode ||
            '',
        ).trim()
      : String(regionStore.zipcode ?? '').trim();
    const subregion = useMulti ? subregionNameVal || subregionIbgeVal : '';
    regionStore.setDeliveryRegion({
      region: deliveryRegion,
      subregion,
      zipcode,
    });
    if (useMulti && subregionNameVal && subregionIbgeVal) {
      regionStore.$patch({ subregionIbge: subregionIbgeVal });
    }
    track('modal_local_entrega_confirmar', {
      stateSelected: region.value,
      cityId: subregionOptionValue.value,
      cityName: subregionNameInput.value,
    });
  }

  function cancelClearCartModal() {
    syncModalFieldsFromStore();
    clearCartModal.value = false;
  }

  const {
    mutate: removeItemCart,
    loading: removeItemCartLoading,
    onDone: removeItemCartResult,
    onError: removeItemCartError,
  } = useRemoveProductCartMutation({
    context: {
      headers: {
        'x-impersonate-id': userStore.personifiedUserId,
      },
    },
  });

  const {
    mutate: removeAllItemsCart,
    loading: removeAllItemsCartLoading,
    onDone: removeAllCartResult,
    onError: removeAllCartError,
  } = useRemoveAllProductsCartMutation({
    context: {
      headers: {
        'x-impersonate-id': userStore.personifiedUserId,
      },
    },
  });

  const handleSuccess = () => redirectToHome();
  const handleError = () => redirectToHome();

  removeItemCartResult(handleSuccess);
  removeItemCartError(handleError);
  removeAllCartResult(handleSuccess);
  removeAllCartError(handleError);
  const clearCartLoading = computed(
    () => removeItemCartLoading.value || removeAllItemsCartLoading.value,
  );

  function removeCartAction() {
    const headers = {
      'x-impersonate-id': userStore.personifiedUserId,
    };

    if (!cartData.value) {
      redirectToHome();
      return;
    }

    const items = cartData.value?.items;

    if (!items || items.length === 0) {
      removeAllItemsCart(
        {
          cart_id: cartData.value!.id,
        },
        {
          context: { headers },
        },
      );
    } else {
      removeItemCart(
        {
          cart_id: cartData.value!.id,
          sku: cartData.value?.items![0]!.sku,
        },
        {
          context: { headers },
        },
      );
    }
  }

  const isConfirmDisabled = computed(
    () =>
      !regionInput.value ||
      (multiCd.value && !String(subregionOptionValue.value || '').trim()) ||
      updateSubregionLoading.value,
  );
</script>

<template>
  <div>
    <SharedTooltip position="bottom" class="w-full">
      <template #text>
        <div class="text-tooltip">
          A região de entrega define <strong>preços</strong>,
          <strong>prazos</strong> e <strong>disponibilidade</strong> dos
          produtos exibidos na loja.
        </div>
      </template>
      <div
        class="store-state-selection-menu-action"
        role="button"
        tabindex="0"
        @click="handleLocationModal"
        @keydown.enter="handleLocationModal"
        @keydown.space.prevent="handleLocationModal">
        <icon-girassol:location-on class="icon" />
        <div class="info">
          <template v-if="regionStore.loadingRegion">
            <SharedSkeleton class="h-3 w-28" />
            <SharedSkeleton class="h-3 w-36" />
          </template>
          <template v-else-if="headerStateName && !headerSubregionName">
            <span class="title">Estado de entrega</span>
            <span class="subtitle">{{ headerStateName }}</span>
          </template>
          <template v-else-if="headerStateName && headerSubregionName">
            <span class="title">{{ headerStateName }}</span>
            <span class="subtitle">{{ headerSubregionName }}</span>
          </template>
        </div>
      </div>
      <div
        class="store-state-selection-menu-action-mobile"
        role="button"
        tabindex="0"
        @click="handleLocationModal"
        @keydown.enter="handleLocationModal"
        @keydown.space.prevent="handleLocationModal">
        <div v-if="regionStore.loadingRegion" class="content">
          <SharedSkeleton class="h-3 w-40" />
        </div>
        <div v-else class="content">
          <icon-girassol:location-on
            id="location-icon-test"
            class="icon-left" />
          <span class="subtitle">{{ headerMobileLine }}</span>
          <icon-girassol:chevron-right
            id="chevron-icon-test"
            class="icon-right" />
        </div>
      </div>
    </SharedTooltip>

    <SolModal
      id="location-modal"
      :is-open="regionModal"
      action-secondary-text="Cancelar"
      title="Selecione a região de entrega"
      :class="[
        'state-selection-menu__modal',
        { 'location-modal--select-open': isSelectOpen },
      ]"
      :size="{
        desktop: 'small',
        mobile: 'bottom-sheet',
      }"
      @action:secondary="handleLocationModal"
      @close="handleLocationModal">
      <div
        class="description flex w-full max-w-full flex-col gap-4xs"
        :class="{ 'location-modal--select-open': isSelectOpen }">
        <p v-if="multiCd" class="location-modal__lead m-0">
          Escolha o estado e a cidade para exibirmos corretamente os
          <strong>preços</strong>, a <strong>disponibilidade</strong> e os
          <strong>prazos de entrega</strong> dos produtos.
        </p>
        <p v-else class="location-modal__lead m-0">
          Escolha o estado para exibirmos corretamente os
          <strong>preços</strong>, a <strong>disponibilidade</strong> e os
          <strong>prazos de entrega</strong> dos produtos.
        </p>
        <SolSelect
          id="state"
          name="state"
          label="Estado"
          class="inputSelect"
          :options="stateSelectOptionsFiltered"
          placeholder="Selecione o estado"
          :searchable="true"
          :fetch-on-search="false"
          :selected="stateSelected"
          @open-dropdown="onRegionSelectOpen"
          @close-dropdown="onRegionSelectClose"
          @search="onStateSearchInput"
          @update:selected="changeStateInput" />
        <SolSelect
          v-if="multiCd"
          id="subregion"
          :key="regionInput || 'subregion'"
          name="subregion"
          label="Cidade"
          class="inputSelect"
          :disabled="!regionInput"
          :options="subregionOptions"
          :loading="loadingCities"
          :searchable="true"
          :fetch-on-search="true"
          :placeholder="subregionNameInput || 'Selecione a cidade'"
          v-model:selected="subregionSelected"
          @open-dropdown="onSubregionSelectOpen"
          @close-dropdown="onSubregionSelectClose"
          @search="onSubregionSearchInput" />
      </div>
      <template #action-primary>
        <SolButton
          id="btn-alterar-regiao-entrega"
          variant="primary"
          size="medium"
          :disabled="isConfirmDisabled"
          :loading="updateSubregionLoading"
          @click="changeLocation">
          Alterar região de entrega
        </SolButton>
      </template>
    </SolModal>

    <SolModal
      id="clear-cart-modal"
      :is-open="clearCartModal"
      action-secondary-text="Não, cancelar"
      title="Alteração da região de entrega"
      :size="{
        desktop: 'small',
        mobile: 'bottom-sheet',
      }"
      @action:primary="removeCartAction"
      @action:secondary="cancelClearCartModal"
      @close="cancelClearCartModal">
      <template #action-primary>
        <SolButton
          id="btn-clear-cart-confirm"
          variant="primary"
          size="medium"
          :loading="clearCartLoading"
          @click="removeCartAction">
          Sim, alterar região de entrega
        </SolButton>
      </template>
      <p>
        A mudança de <strong>estado</strong
        ><span v-if="multiCd"> e/ou <strong>cidade</strong></span> feita,
        alterou o centro de distribuição que atende a região escolhida.
      </p>

      <p>
        Para seguir com a nova região de entrega, vamos precisar limpar o
        carrinho.
      </p>

      <p>Você deseja seguir com a alteração?</p>
    </SolModal>
  </div>
</template>

<style lang="scss">
  .text-tooltip {
    @apply fonts-body-medium-regular text-center w-[16.25rem];
  }

  .store-state-selection-menu-action {
    @apply hidden md:site:flex items-center gap-nano;
    @apply py-4xs px-micro;
    @apply cursor-pointer;

    > .icon {
      @apply icon-size-medium;
      @apply text-neutral-low-dark;
    }

    > .info {
      @apply flex flex-col items-start;

      > .title {
        @apply fonts-body-small-regular;
        @apply text-neutral-low-light;
      }

      > .subtitle {
        @apply fonts-body-small-bold;
        @apply text-neutral-low-dark;
      }
    }
  }

  .store-state-selection-menu-action-mobile {
    @apply flex md:site:hidden align-middle bg-neutral-high-light text-neutral-low-dark py-2 px-3;

    > .content {
      @apply w-full gap-x-1 flex relative cursor-pointer;

      > .icon-left {
        @apply icon-size-small inline;
      }

      > .title {
        @apply fonts-body-small-regular;
      }

      > .subtitle {
        @apply fonts-body-small-bold;
      }

      > .icon-right {
        @apply icon-size-small absolute right-0;
      }
    }
  }

  .state-selection-menu__modal.sol-modal-core {
    @apply rounded-b-none sm:site:rounded-sm;

    .footer {
      @apply px-micro py-3xs;
    }

    .footer > .main-action {
      @apply mb-0;
    }
  }

  .state-selection-menu__modal.location-modal--select-open.sol-modal-core {
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;

    :deep(.content),
    :deep([class*='scroll']) {
      overflow: visible !important;
      max-height: none !important;
    }
  }

  .description.location-modal--select-open {
    @apply min-h-[min(32rem,88vh)] sm:site:min-h-[32.5rem] relative z-10;
    overflow: visible;
  }

  .location-modal__lead {
    @apply fonts-body-large-regular text-neutral-low-dark;

    strong {
      @apply fonts-body-large-bold;
    }
  }
</style>
